// src/components/SocialPurchaseModal.jsx
import { useState } from "react";
import { X, Copy, CheckCheck, ShoppingCart, AlertTriangle, Wallet } from "lucide-react";
import Currency from "./Currency";
import { formatShopviaCredential, credentialRowsToText } from "../utils/credentialFormat";

// ── buildCredentialLine ───────────────────────────────────────────────────────
// Handles all 3 purchase sources:
//
// manual:   credentials.email + password + extra → "email:password:extra"
// vaultacc: full log in credentials.email, password/extra null → raw string
// sujan:    full credential string in credentials.email (tab-separated from API)
//           password/extra null → displayed as-is
// ─────────────────────────────────────────────────────────────────────────────
function buildCredentialLine(credentials, previewUrl) {
  if (!credentials) return previewUrl || "No credentials available";

  // VaultAcc + Sujan: full content is in credentials.email, nothing else set
  if (credentials.email && !credentials.password && !credentials.extra) {
    const line = credentials.email;
    if (previewUrl) return `${line}\n${previewUrl}`;
    return line;
  }

  // Manual DB accounts: assemble from individual fields
  const parts = [];
  if (credentials.email)    parts.push(credentials.email);
  if (credentials.password) parts.push(credentials.password);
  if (credentials.extra)    parts.push(credentials.extra);
  if (previewUrl)           parts.push(previewUrl);
  return parts.length > 0 ? parts.join(":") : "No credentials available";
}

export default function SocialPurchaseModal({
  group,
  quantity,
  walletBalance,
  purchasing,
  purchaseError,
  onConfirm,
  onClose,
  onGoToWallet,
}) {
  const [purchased, setPurchased] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [copied,    setCopied]    = useState({});

  const totalPrice   = group.priceNgn * quantity;
  const insufficient = walletBalance < totalPrice;
  const shortfall    = totalPrice - walletBalance;

  const handleConfirm = async () => {
    const result = await onConfirm();
    if (result) { setPurchases(result); setPurchased(true); }
  };

  const copyLine = (id, line) => {
    navigator.clipboard.writeText(line).then(() => {
      setCopied(prev => ({ ...prev, [id]: true }));
      setTimeout(() => setCopied(prev => ({ ...prev, [id]: false })), 2000);
    });
  };

  const copyAll = () => {
    const allLines = purchases.map(p => buildCredentialLine(p.credentials, p.previewUrl)).join("\n\n");
    navigator.clipboard.writeText(allLines).then(() => {
      setCopied({ all: true });
      setTimeout(() => setCopied({}), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={!purchased ? onClose : undefined} />

      <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <h2 className="font-semibold text-gray-900 dark:text-white text-base">
            {purchased ? "Purchase Complete" : "Confirm Purchase"}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {!purchased ? (
            <>
              {/* Summary */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mb-4">
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">{group.title}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 capitalize">
                  {group.category}{quantity > 1 ? ` · ${quantity} items` : ""}
                </p>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {quantity > 1 ? `Price (×${quantity})` : "Price"}
                    </span>
                    <span className="text-base font-bold text-purple-600 dark:text-purple-400">
                      <Currency amount={totalPrice} />
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Your balance</span>
                    <span className={`text-sm font-medium ${insufficient ? "text-red-500" : "text-green-600 dark:text-green-400"}`}>
                      <Currency amount={walletBalance} />
                    </span>
                  </div>
                  {insufficient ? (
                    <div className="flex items-center justify-between pt-1.5 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-xs font-medium text-red-500">You need</span>
                      <span className="text-sm font-bold text-red-500">
                        + <Currency amount={shortfall} />
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between pt-1.5 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-xs text-gray-500 dark:text-gray-400">After purchase</span>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        <Currency amount={walletBalance - totalPrice} />
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Insufficient balance block */}
              {insufficient && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-2 mb-3">
                    <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-red-600 dark:text-red-400">Insufficient balance</p>
                      <p className="text-xs text-red-500 dark:text-red-400 mt-0.5">
                        You need <span className="font-bold">₦{shortfall.toLocaleString()}</span> more to complete this purchase.
                        Fund your wallet to continue.
                      </p>
                    </div>
                  </div>
                  <button onClick={onGoToWallet}
                    className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold bg-red-600 hover:bg-red-700 text-white rounded-xl transition">
                    <Wallet size={15} />Fund Wallet Now
                  </button>
                </div>
              )}

              {/* Purchase error */}
              {purchaseError && (
                <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 mb-4">
                  <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-red-600 dark:text-red-400">Purchase failed</p>
                    <p className="text-xs text-red-500 dark:text-red-400 mt-0.5">{purchaseError}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Your wallet has not been charged. Please try again.
                    </p>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-400 dark:text-gray-500 mb-5 leading-relaxed">
                ⚠️ All sales are final. Credentials will be revealed immediately after payment.
              </p>

              <div className="flex gap-3">
                <button onClick={onClose}
                  className="flex-1 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                  Cancel
                </button>
                <button onClick={handleConfirm} disabled={purchasing || insufficient}
                  className="flex-1 py-2.5 text-sm font-medium bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-xl transition flex items-center justify-center gap-2">
                  {purchasing ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Processing...</>
                  ) : (
                    <><ShoppingCart size={15} />{purchaseError ? "Try Again" : "Confirm Buy"}</>
                  )}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Success state */}
              <div className="text-center mb-5">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCheck size={24} className="text-green-600 dark:text-green-400" />
                </div>
                <p className="text-sm font-bold text-gray-900 dark:text-white">
                  {purchases.length} item{purchases.length !== 1 ? "s" : ""} purchased!
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Save your credentials below. They're also saved in My Purchases.
                </p>
              </div>

              {purchases.length > 1 && (
                <button onClick={copyAll}
                  className="w-full flex items-center justify-center gap-2 py-2 mb-4 text-xs font-medium text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 rounded-xl hover:bg-purple-50 dark:hover:bg-purple-900/20 transition">
                  {copied.all ? <CheckCheck size={13} className="text-green-500" /> : <Copy size={13} />}
                  {copied.all ? "Copied all!" : `Copy all ${purchases.length} credentials`}
                </button>
              )}

              <div className="space-y-3 mb-5">
                {purchases.map((purchase, index) => {
                  const rawLine = buildCredentialLine(purchase.credentials, purchase.previewUrl);
                  const id      = purchase._id || index;

                  const parsed = purchase.source === "shopvia"
                    ? formatShopviaCredential(purchase.credentials?.email, purchase.shopviaDescription)
                    : { type: "raw", text: rawLine };

                  const copyText = parsed.type === "labeled" ? credentialRowsToText(parsed.rows) : parsed.text;

                  return (
                    <div key={id} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          {purchase.source === "sujan" ? "VPN / App" : `Account ${index + 1}`}
                        </span>
                        <button onClick={() => copyLine(id, copyText)}
                          className="flex items-center gap-1 px-2 py-1 text-xs text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition">
                          {copied[id] ? <CheckCheck size={12} className="text-green-500" /> : <Copy size={12} />}
                          {copied[id] ? "Copied!" : "Copy"}
                        </button>
                      </div>
                      <div className="bg-white dark:bg-gray-900 rounded-lg p-2 border border-gray-200 dark:border-gray-700">
                        {parsed.type === "labeled" ? (
                          <div className="space-y-1">
                            {parsed.rows.map((r, i) => (
                              <p key={i} className="text-xs text-gray-800 dark:text-gray-200 leading-relaxed">
                                <span className="font-semibold text-gray-500 dark:text-gray-400">{r.label}:</span>{" "}
                                <span className="font-mono break-all">{r.value}</span>
                              </p>
                            ))}
                          </div>
                        ) : (
                          <p className="font-mono text-xs text-gray-800 dark:text-gray-200 break-all leading-relaxed whitespace-pre-wrap">
                            {copyText || "No credentials available — please contact support"}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <button onClick={onClose}
                className="w-full py-2.5 text-sm font-medium bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition">
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}